// Programa de pontos (Ultra, 05/10/2026) no cliente: tipos da resposta de /api/catalog/:slug/loyalty e textos curtos
// que o link e o painel repetem. A conta (quanto rende, quanto desconta) é a de @workspace/pricing loyaltyMath.
import type { LoyaltyRewardType } from "@workspace/pricing";

export interface PublicReward {
  id: string;
  name: string;
  type: LoyaltyRewardType;
  cost: number;
  value: number;
  stockLeft: number | null;
}

export interface PublicProgram {
  earnMode: "real" | "pedido";
  pointsPerReal: number;
  /** "a cada R$ X" do modo por real; ausente na API de antes da opção = R$ 1. */
  realStep?: number;
  pointsPerOrder: number;
  minOrder: number;
  validityDays: number | null;
  rewards: PublicReward[];
  earnWays: Array<{ id: string; name: string; points: number }>;
  bonusFirstOrder: number;
  bonusBirthday: number;
  doubleDays: number[];
  referralPoints: number;
  oneRewardPerOrder: boolean;
  allowWithCoupon: boolean;
}

export interface LedgerLine {
  id: string;
  kind: string;
  points: number;
  description: string;
  at: string;
  expiresAt: string | null;
  open: boolean;
  reversed: boolean;
  orderId: string | null;
}

export interface StoreLoyalty {
  enabled: boolean;
  program?: PublicProgram;
  loggedIn?: boolean;
  verified?: boolean;
  balance?: number;
  expiring?: Array<{ points: number; date: string }>;
  lines?: LedgerLine[];
  won?: Array<{ name: string; at: string; points: number; orderId: string | null }>;
  referral?: { code: string } | null;
}

export const DIAS_SEMANA = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];

export function fmtPontos(n: number): string {
  const v = Math.trunc(n || 0);
  return `${v.toLocaleString("pt-BR")} ${Math.abs(v) === 1 ? "ponto" : "pontos"}`;
}

export function fmtPts(n: number): string {
  const v = Math.trunc(n || 0);
  return `${v.toLocaleString("pt-BR")} ${Math.abs(v) === 1 ? "pt" : "pts"}`;
}

/** "12/11" a partir de YYYY-MM-DD ou ISO. */
export function diaMes(d: string): string {
  const s = d.length >= 10 ? d.slice(0, 10) : d;
  const [, m, dd] = s.split("-");
  return m && dd ? `${dd}/${m}` : s;
}

/** Como o cliente ganha, em uma frase curta: "R$ 1 = 1 ponto" / "R$ 15 = 1 ponto" / "1 ponto por pedido". */
export function regraDeGanho(p: Pick<PublicProgram, "earnMode" | "pointsPerReal" | "realStep" | "pointsPerOrder" | "minOrder">): string {
  const passo = Math.max(1, Number(p.realStep) || 1);
  const base =
    p.earnMode === "pedido"
      ? `${fmtPontos(p.pointsPerOrder)} por pedido`
      : `R$ ${passo.toFixed(2).replace(".", ",").replace(",00", "")} = ${fmtPontos(p.pointsPerReal)}`;
  return p.minOrder > 0
    ? `${base} (pedidos a partir de R$ ${p.minOrder.toFixed(2).replace(".", ",")})`
    : base;
}

/** Prêmio que o saldo alcança primeiro (para o "Faltam N para …"). */
export function proximoPremio(rewards: PublicReward[], saldo: number): { reward: PublicReward; faltam: number } | null {
  const alvo = rewards
    .filter((r) => r.type !== "cashback" && r.cost > saldo && (r.stockLeft == null || r.stockLeft > 0))
    .sort((a, b) => a.cost - b.cost)[0];
  return alvo ? { reward: alvo, faltam: alvo.cost - saldo } : null;
}

export const TIPO_PREMIO: Record<LoyaltyRewardType, string> = {
  fixed: "Desconto em R$",
  percent: "Desconto em %",
  free_delivery: "Frete grátis",
  gift: "Brinde",
  cashback: "Cashback",
};

/** Chave do localStorage onde o link guarda o código de quem indicou (?ind=). */
export const chaveIndicacao = (slug: string) => `vnz-ind-${slug}`;
/** Prêmio escolhido em "Usar" (Minha conta) para o checkout pré-selecionar. */
export const chavePremio = (slug: string) => `vnz-premio-${slug}`;

/** Dia da semana de hoje no fuso da loja (0 = domingo) — o "em dobro" é do dia do pedido. */
export function diaDaSemanaNaLoja(tz: string, agora: Date = new Date()): number {
  const nome = new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "short" }).format(agora);
  return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(nome);
}
